import React, {
	useRef,
	createContext,
	useContext,
	type PropsWithChildren,
	useCallback,
	useMemo,
} from 'react';
import { graphql, useFragment, useRelayEnvironment } from 'react-relay/hooks';
import { componentWithCondition } from '@atlassian/jira-feature-flagging-utils';
import { fg } from '@atlassian/jira-feature-gating';
import type { Props as AsyncColumnPickerProps } from '@atlassian/jira-issue-table-column-picker/src/ui/main.tsx';
import type { optimisticUpdatesSelectedOptionsQuery$key as IFCOptimisticUpdatesKey } from '@atlassian/jira-relay/src/__generated__/optimisticUpdatesSelectedOptionsQuery.graphql';
import { KNOWN_COLUMN_TYPES } from '@atlassian/jira-issue-table-common/src/constants.tsx';
import type { context_nativeIssueTable_IFCConfigContextProvider_queryRef$key as IFCConfigContextProviderKey } from '@atlassian/jira-relay/src/__generated__/context_nativeIssueTable_IFCConfigContextProvider_queryRef.graphql';
import type { context_nativeIssueTable_IFCConfigContextProvider_columnsQueryRef$key as FieldConfigKey } from '@atlassian/jira-relay/src/__generated__/context_nativeIssueTable_IFCConfigContextProvider_columnsQueryRef.graphql';
import { deleteOptimisticFieldConfig } from '@atlassian/jira-issue-table-column-picker/src/ui/utils.tsx';
import type { IFCConfigType } from '../types.tsx';
import type { Column } from '../../../types.tsx';
import { COLUMN_ID_INTERNAL_COLUMN } from '../../../constants.tsx';
import {
	TEMP_CREATE_FIELDSET_ID,
	updateOptimisticFieldName,
	updateOptimisticFieldSetIdByFieldId,
} from '../column-picker-footer/utils.tsx';
import {
	getSelectedOptions,
	optimisticUpdatesSelectedOptions,
	optimisticUpdateFieldSetId,
} from '../optimistic-updates/index.tsx';

type IFCConfigContextType = {
	ifcConfig: IFCConfigType;
	addFieldToColumns?: (
		fieldSetId: string,
		index?: number,
		onErrorCallback?: () => void,
		update?: boolean,
	) => void;
	updateSelectedOptions?: (
		fieldId: string,
		type: string,
		options: { id: string; value: string }[],
	) => void;
	optimisticUpdateField?: (
		fieldId: string,
		field: {
			name: string;
			type: string;
			options?: { id: string; value: string }[] | undefined;
		},
	) => void;
	replaceFieldSetInColumns?: (
		fieldSetId: string,
		newfieldSetId: string,
		fieldId: string,
		onSuccessCallback?: () => void,
		onErrorCallback?: () => void,
	) => void;
	optimisticDeleteField?: (newfieldSetId: string) => void;
};

export const IFCConfigContext = createContext<IFCConfigContextType>({ ifcConfig: undefined });

export const IFCConfigContextProvider = ({
	children,
	ifcConfig,
	columns,
	onColumnsChange,
	queryRef,
	columnsQueryRef,
}: PropsWithChildren<{
	ifcConfig: IFCConfigType;
	columns: Column[];
	onColumnsChange: AsyncColumnPickerProps['onChange'];
	queryRef: IFCConfigContextProviderKey | null;
	columnsQueryRef: FieldConfigKey | null;
}>) => {
	const columnRef = useRef(columns);

	const currentIssuesRef = useRef<IFCOptimisticUpdatesKey | null>(null);

	const processedIssuesRef = useRef<{
		issuesRef: IFCOptimisticUpdatesKey | null;
		fieldId: string | null;
	}>({
		issuesRef: null,
		fieldId: null,
	});

	const selectedOptions = useRef<{ [key: string]: string } | undefined>(undefined);

	if (columnRef.current !== columns) {
		columnRef.current = columns;
	}

	const environment = useRelayEnvironment();

	const issuesQueryRef = useFragment<IFCConfigContextProviderKey>(
		graphql`
			fragment context_nativeIssueTable_IFCConfigContextProvider_queryRef on JiraIssueConnection {
				...optimisticUpdatesSelectedOptionsQuery
			}
		`,
		queryRef,
	);

	const fieldConfig = useFragment<FieldConfigKey>(
		graphql`
			fragment context_nativeIssueTable_IFCConfigContextProvider_columnsQueryRef on JiraIssueSearchFieldSetConnection {
				__id
			}
		`,
		columnsQueryRef,
	);

	if (currentIssuesRef.current !== issuesQueryRef) {
		currentIssuesRef.current = issuesQueryRef ?? null;
	}

	const replaceFieldSetInColumns = useCallback(
		(
			fieldSetId: string,
			newfieldSetId: string,
			fieldId: string,
			onSuccessCallback?: () => void,
			onErrorCallback?: () => void,
		) => {
			updateOptimisticFieldSetIdByFieldId({
				environment,
				fieldConfigId: fieldConfig?.__id,
				field: {
					fieldId,
					fieldSetId: newfieldSetId,
				},
			});

			const columnIds = columnRef.current
				?.map(({ id }) => id)
				?.filter(
					(id) => !id.startsWith(COLUMN_ID_INTERNAL_COLUMN) && id !== TEMP_CREATE_FIELDSET_ID,
				);

			const insertIndex = columnIds.findIndex((id) => id === fieldSetId);
			optimisticUpdateFieldSetId(environment, currentIssuesRef.current, fieldId, newfieldSetId);
			if (insertIndex > -1) {
				columnIds[insertIndex] = newfieldSetId;
				onColumnsChange?.({
					columnIds,
					retries: 3,
					onSuccessCallback,
					onErrorCallback,
					isHorizontalAutoScrollNeeded: false,
					skipRefetch: true,
				});
			}
		},
		[environment, fieldConfig?.__id, onColumnsChange],
	);

	const addFieldToColumns = useCallback(
		(fieldSetId: string, index?: number, onErrorCallback?: () => void) => {
			const columnIds = columnRef.current
				?.map(({ id }) => id)
				?.filter(
					(id) => !id.startsWith(COLUMN_ID_INTERNAL_COLUMN) && id !== TEMP_CREATE_FIELDSET_ID,
				);

			const insertIndex = index == null ? columnIds.length : index;
			columnIds.splice(insertIndex, 0, fieldSetId);
			onColumnsChange?.({
				columnIds,
				retries: 3,
				onErrorCallback,
				isHorizontalAutoScrollNeeded: index === undefined,
			});
		},
		[onColumnsChange],
	);

	const updateSelectedOptions = useCallback(
		(fieldId: string, type: string, options: { id: string; value: string }[]) => {
			if (
				type !== KNOWN_COLUMN_TYPES.CHECKBOX_SELECT &&
				type !== KNOWN_COLUMN_TYPES.SINGLE_SELECT
			) {
				return;
			}

			if (
				processedIssuesRef.current.issuesRef !== currentIssuesRef.current ||
				processedIssuesRef.current.fieldId !== fieldId
			) {
				selectedOptions.current = getSelectedOptions(currentIssuesRef.current, fieldId, type);
				processedIssuesRef.current.issuesRef = currentIssuesRef.current;
				processedIssuesRef.current.fieldId = fieldId;
			}

			if (selectedOptions.current) {
				optimisticUpdatesSelectedOptions(environment, type, selectedOptions.current, options);
			}
		},
		[environment],
	);

	const optimisticUpdateField = useCallback(
		(
			fieldId: string,
			field: {
				name: string;
				type: string;
				options?: { id: string; value: string }[] | undefined;
			},
		) => {
			if (fieldConfig?.__id) {
				updateOptimisticFieldName({
					environment,
					fieldConfigId: fieldConfig?.__id,
					field: {
						fieldId,
						name: field.name,
					},
				});

				if (field.options) {
					updateSelectedOptions?.(fieldId, field.type, field.options);
				}
			}
		},
		[environment, fieldConfig?.__id, updateSelectedOptions],
	);

	const optimisticDeleteField = useCallback(
		(fieldSetId: string) => {
			deleteOptimisticFieldConfig({
				environment,
				fieldConfigId: fieldConfig?.__id,
				fields: [
					{
						fieldSetId,
						name: '',
					},
				],
			});
		},
		[environment, fieldConfig?.__id],
	);

	const value = useMemo(
		() => ({
			ifcConfig,
			addFieldToColumns,
			updateSelectedOptions,
			replaceFieldSetInColumns,
			optimisticUpdateField,
			optimisticDeleteField,
		}),
		[
			ifcConfig,
			addFieldToColumns,
			updateSelectedOptions,
			replaceFieldSetInColumns,
			optimisticUpdateField,
			optimisticDeleteField,
		],
	);

	return <IFCConfigContext.Provider value={value}>{children}</IFCConfigContext.Provider>;
};

export const useIFCConfigContext = () => {
	return useContext(IFCConfigContext);
};

export const IFCConfigContextProviderComponent = componentWithCondition(
	() => fg('jira_inline_field_config_gate'),
	IFCConfigContextProvider,
	({ children }: PropsWithChildren<{ ifcConfig: IFCConfigType }>) => children,
);
